import { SdkLogger } from './sdkLogger';
import { notifyCapabilityFailed, notifyCapabilityTriggered } from './capabilityMonitor';

/**
 * Manages window modal method blocking and logging.
 * Stores original methods in constructor and provides methods to block, log, or restore them.
 */
export class WindowMethodBlocker {
  private readonly originalMethods: {
    alert: typeof window.alert;
    confirm: typeof window.confirm;
    prompt: typeof window.prompt;
  };
  private isBlocking: boolean = false;

  constructor(private readonly windowObj: Window, private readonly logger: SdkLogger) {
    // Store original methods when instance is created
    this.originalMethods = {
      alert: windowObj.alert,
      confirm: windowObj.confirm,
      prompt: windowObj.prompt,
    };
    try {
      // Set up overrides once
      this.setupOverrides();
    } catch (error) {
      logger.warn('Failed to setup window method overrides', { error });
    }
  }
  /**
   * Sets up method overrides that check the blocking flag.
   * Methods are overridden only once in the constructor.
   */
  private setupOverrides(): void {
    // Override alert to log and conditionally call original
    this.windowObj.alert = (...args: Parameters<typeof window.alert>): void => {
      notifyCapabilityTriggered(this.logger, 'modal-alert');

      if (!this.isBlocking) {
        return this.originalMethods.alert.call(this.windowObj, ...args);
      }

      notifyCapabilityFailed(this.logger, 'modal-alert');

      // eslint-disable-next-line no-console
      console.warn(
        'Vim OS SDK: To avoid blocking the user while working in the EHR, window popup functions are not allowed. The action was blocked.',
      );
    };

    // Override confirm to log and conditionally call original
    this.windowObj.confirm = (...args: Parameters<typeof window.confirm>): boolean => {
      notifyCapabilityTriggered(this.logger, 'modal-confirm');

      if (!this.isBlocking) {
        return this.originalMethods.confirm.call(this.windowObj, ...args);
      }

      notifyCapabilityFailed(this.logger, 'modal-confirm');

      // eslint-disable-next-line no-console
      console.warn(
        'Vim OS SDK: To avoid blocking the user while working in the EHR, window popup functions are not allowed. The action was blocked.',
      );
      return false;
    };

    // Override prompt to log and conditionally call original
    this.windowObj.prompt = (...args: Parameters<typeof window.prompt>): string | null => {
      notifyCapabilityTriggered(this.logger, 'modal-prompt');

      if (!this.isBlocking) {
        return this.originalMethods.prompt.call(this.windowObj, ...args);
      }

      notifyCapabilityFailed(this.logger, 'modal-prompt');

      // eslint-disable-next-line no-console
      console.warn(
        'Vim OS SDK: To avoid blocking the user while working in the EHR, window popup functions are not allowed. The action was blocked.',
      );
      return null;
    };
  }

  /**
   * Sets the blocking state.
   * If true, modal methods will be logged but not executed.
   * If false, modal methods will be logged and executed normally.
   */
  setBlockState(shouldBlock: boolean): void {
    this.isBlocking = shouldBlock;
  }

  /**
   * Restores original window modal methods, removing all overrides.
   */
  restore(): void {
    this.windowObj.alert = this.originalMethods.alert;
    this.windowObj.confirm = this.originalMethods.confirm;
    this.windowObj.prompt = this.originalMethods.prompt;
  }
}
